import { axiosInstance } from "./index";

export type StatementStatus = "issued" | "confirmed" | "rejected" | "superseded";
export type PayeeEntityType = "individual" | "company" | "trust";

export interface StatementPayee {
	legalName: string;
	entityType: PayeeEntityType;
	addressLine1: string | null;
	addressLine2: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	countryCode: string | null;
	/** Country of tax residence from the declaration; the country whose rate applied. */
	taxCountryCode: string;
	/** Present only when the creator declared tax registration. */
	registrationNumber: string | null;
	registrationNumberLabel: string | null;
	bankName: string;
	/** Exactly the last four digits; the view prefixes the mask. */
	accountLast4: string;
}

export interface StatementDeliverable {
	id: string;
	type: string;
	title: string;
	quantity: number;
}

/**
 * A Payment Statement exactly as issued. Every value shown comes from this object —
 * the backend copies it onto the row at issue and it never changes (Ticket-348).
 */
export interface PaymentStatement {
	id: string;
	statementNumber: string;
	paymentInstructionId: string;
	campaignEngagementId: string;
	userId: string;
	taxDeclarationId: string;
	countryConfigId: string;
	paymentProfileId: string;
	issuerName: string;
	issuerRegistration: string;
	issuerAddress: string;
	payee: StatementPayee;
	campaignName: string;
	projectCode: string;
	marketName: string | null;
	deliverables: StatementDeliverable[];
	currency: string;
	principalAmount: number;
	taxName: string | null;
	taxNameLong: string | null;
	taxAuthorityName: string | null;
	taxRate: number | null;
	declaredTaxAmount: number | null;
	amountPayable: number;
	paymentTermsDays: number;
	creatorDeclarationText: string;
	footerText: string;
	restatementText: string | null;
	templateVersion: string;
	status: StatementStatus;
	confirmedAt: string | null;
	rejectedAt: string | null;
	rejectionReason: string | null;
	supersededAt: string | null;
	supersededById: string | null;
	issuedAt: string;
	issuedBy: string | null;
}

/** The document without its identity, status or issue record: what a preview shows and what gets frozen at issue. */
export type StatementContent = Omit<
	PaymentStatement,
	| "id"
	| "statementNumber"
	| "status"
	| "issuedAt"
	| "issuedBy"
	| "confirmedAt"
	| "rejectedAt"
	| "rejectionReason"
	| "supersededAt"
	| "supersededById"
>;

/** What the CM sees before sending to Finance: the would-be statement, or why the send is blocked. */
export type StatementPreview =
	| { content: StatementContent }
	| { statement: PaymentStatement }
	| {
			blocked:
				| "TAX_DECLARATION_MISSING"
				| "BANK_PROFILE_MISSING"
				| "COUNTRY_NOT_CONFIGURED"
				| "LOCAL_PARTNER_ROUTE";
			message: string;
	  };

export const paymentStatementAPI = {
	listMine: async (): Promise<PaymentStatement[]> => (await axiosInstance.get("/creators/me/statements")).data,
	getMine: async (id: string): Promise<PaymentStatement> =>
		(await axiosInstance.get(`/creators/me/statements/${id}`)).data,
	getForCampaignCreator: async (campaignId: string, creatorBlockId: string): Promise<PaymentStatement> =>
		(await axiosInstance.get(`/campaigns/${campaignId}/creator-blocks/${creatorBlockId}/statement`)).data,
	preview: async (campaignId: string, creatorBlockId: string): Promise<StatementPreview> =>
		(await axiosInstance.get(`/campaigns/${campaignId}/creator-blocks/${creatorBlockId}/statement/preview`)).data,
	getForFinancePayment: async (paymentId: string): Promise<PaymentStatement> =>
		(await axiosInstance.get(`/agency/finance/payments/${paymentId}/statement`)).data,
};
